import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as FileUploadIcon } from '../../../../assets/icons/file-input.svg';
import { ReactComponent as RemoveIcon } from '../../../../assets/icons/remove-icon.svg';
import { ReactComponent as UploadIcon } from '../../../../assets/icons/upload-icon.svg';
import { useAppDispatch } from '../../../../redux/hooks';
import { editModel, fetchAllModels } from '../../../../redux/slices/ModelSlice';
import { setNotificationDetails } from '../../../../redux/slices/NotificationSlice';
import MyButton from '../../../../shared-components/button/Button';
import CheckBoxButtons from '../../../../shared-components/check-box-buttons/CheckBoxButtons';
import CustomInput from '../../../../shared-components/custom-input/CustomInput';
import ModalBox from '../../../../shared-components/modal-box/ModalBox';
import SelectField from '../../../../shared-components/select-field/SelectField';
import SuccessNotification from '../../../../shared-components/success-notification/SuccessNotification';
import { TooltipIcon } from '../../../../shared-components/tooltip/TooltipIcon';
import deviceApi from '../../../../utils/api/device-hub';
// ⚠ SECURITY PROFILES DISABLED — backend not deployed yet (frontend-only rollout, PR #1130/#1143).
// Re-enable by uncommenting this block once backend security endpoints/fields ship.
/*
import ModelSecurityFields from '../security/ModelSecurityFields';
import {
  ModelSecurityFormState,
  appendSecurityToFormData,
  buildModelSecurityPayload,
  getEmptyModelSecurityForm,
  isModelSecurityError,
  toModelSecurityForm,
  validateModelSecurityForm,
} from '../security/securityProfile';
*/

interface BrandsListInterface {
  companyName?: string;
  name?: string;
  _id: string;
  source?: string;
}



/** Allowed values for the model's diagnosis protocol types multi-select */
const DIAGNOSTIC_PROTOCOL_TYPE_OPTIONS = [
  { label: 'HTTPS', value: 'HTTPS' },
  { label: 'FTPS', value: 'FTPS' },
  { label: 'HTTP', value: 'HTTP' },
  { label: 'FTP', value: 'FTP' },
];

/** Allowed values for the model's firmware protocol types multi-select */
const FIRMWARE_PROTOCOL_TYPE_OPTIONS = [
  { label: 'HTTPS', value: 'HTTPS' },
  { label: 'FTPS', value: 'FTPS' },
  { label: 'HTTP', value: 'HTTP' },
  { label: 'FTP', value: 'FTP' },
];

interface EditModelDrawerProperties {
  /** Whether the modal is open */
  open: boolean;
  /** The model record to edit */
  modelData: any;
  /** Callback to close the modal */
  onClose: () => void;
  /** Called after the model is successfully updated */
  onModelUpdated: () => void;
}

/** Allowed image MIME types for model media upload */
const ALLOWED_IMAGE_TYPES: string[] = ['image/jpeg', 'image/jpg', 'image/png'];

/** Maximum allowed model image file size in bytes (1MB) */
const MAX_MODEL_IMAGE_SIZE_BYTES = 1024 * 1024;

/**
 * Slide-in drawer modal for editing an existing device model.
 * Pre-fills all fields from the current model record and supports image preview and upload.
 * @param {EditModelDrawerProperties} properties - Component props.
 * @param {boolean} properties.open - The open prop.
 * @param {any} properties.modelData - The modelData prop.
 * @param {Function} properties.onClose - The onClose prop.
 * @param {Function} properties.onModelUpdated - The onModelUpdated prop.
 * @returns {React.JSX.Element} The rendered drawer.
 */
function EditModelDrawer({
  open,
  modelData,
  onClose,
  onModelUpdated,
}: EditModelDrawerProperties): React.JSX.Element {
  const { t } = useTranslation('station');
  const dispatch = useAppDispatch();

  const fileInputReference = useRef<HTMLInputElement | null>(null);

  const [modelName, setModelName] = useState('');
  const [brandSelect, setBrandSelect] = useState('');
  const [brandError, setBrandError] = useState('');
  const [modelNameError, setModelNameError] = useState('');
  const [imageError, setImageError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showFormErrors, setShowFormErrors] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [modelMedia, setModelMedia] = useState<File[]>([]);
  const [existingImage, setExistingImage] = useState('');
  const [newImagePreviewUrl, setNewImagePreviewUrl] = useState<string | null>(null);
  const [brandsList, setBrandsList] = useState<BrandsListInterface[]>([]);
  const [diagnosticProtocolTypes, setDiagnosisProtocolTypes] = useState<string[]>([]);
  const [firmwareProtocolTypes, setFirmwareProtocolTypes] = useState<string[]>([]);
  // ⚠ SECURITY PROFILES DISABLED — backend not deployed yet. See imports above.
  /*
  const [security, setSecurity] = useState<ModelSecurityFormState>(getEmptyModelSecurityForm);
  const [securityError, setSecurityError] = useState('');
  */

  const brandOptions = useMemo(
    () =>
      brandsList.map((brand) => {
        const isGlobal = !brand.source;
        return {
          value: brand._id,
          label: brand.name || brand.companyName || '',
          labelNode: (
            <div className="nd-brand-option">
              <span>{brand.name || brand.companyName || ''}</span>
              <span className={`nd-badge ${isGlobal ? 'global' : 'local'}`}>
                {isGlobal ? t('global') : t('local')}
              </span>
            </div>
          ),
        };
      }),
    [brandsList],
  );

  /**
   * Fetches available brands for the brand select dropdown.
   * @returns {Promise<void>} Resolves when brands are fetched.
   */
  const fetchBrandsList = async (): Promise<void> => {
    try {
      const response = await deviceApi.fetchBrands({
        paginate: false,
        status: ['approved', 'pending'],
      });
      const data = response?.data?.results || response?.data || [];
      setBrandsList(Array.isArray(data) ? data : []);
    } catch (error: any) {
      console.error('Failed to fetch brands:', error);
    }
  };

  /**
   * Effect to manage object URL for the newly selected image preview.
   * Revokes previous object URLs to prevent memory leaks.
   */
  useEffect(() => {
    let objectUrl: string | null = null;
    if (modelMedia.length > 0 && modelMedia[0] instanceof File) {
      objectUrl = URL.createObjectURL(modelMedia[0]);
      setNewImagePreviewUrl(objectUrl);
    } else {
      setNewImagePreviewUrl(null);
    }
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [modelMedia]);

  /** Populate fields whenever the drawer opens with new model data */
  useEffect(() => {
    if (open) {
      fetchBrandsList();

      if (modelData) {
        setModelName(modelData.name || '');
        const resolvedBrandId =
          modelData.brandId?._id || modelData.brandId || modelData.brand?._id || '';
        setBrandSelect(resolvedBrandId);

        const resolvedImageUrl =
          modelData.imageUrl ||
          (Array.isArray(modelData.imageUrls) &&
            (modelData.imageUrls[0]?.signedUrl || modelData.imageUrls[0]?.url)) ||
          (typeof modelData.imageUrls === 'string' ? modelData.imageUrls : '') ||
          '';
        setExistingImage(resolvedImageUrl);
        setModelMedia([]);
        setDiagnosisProtocolTypes(
          Array.isArray(modelData.diagnosticProtocolTypes) ? modelData.diagnosticProtocolTypes : [],
        );
        setFirmwareProtocolTypes(
          Array.isArray(modelData.firmwareProtocolTypes) ? modelData.firmwareProtocolTypes : [],
        );
        // SECURITY PROFILES DISABLED — backend not deployed yet.
        // setSecurity(toModelSecurityForm(modelData.security));
        // setSecurityError('');
        setImageError('');
        setBrandError('');
        setModelNameError('');
        setShowFormErrors(false);
      }
    }
  }, [open, modelData]);

  /**
   * Validates and selects a new image file for the model.
   * @param {File[]} files - The selected file list.
   */
  const handleFileSelect = (files: File[]) => {
    setImageError('');
    if (!files || files.length === 0) return;

    const file = files[0];
    const fileType = file.type?.toLowerCase();
    const fileName = file.name?.toLowerCase() || '';
    const hasValidExtension =
      fileName.endsWith('.jpg') || fileName.endsWith('.jpeg') || fileName.endsWith('.png');

    if (!ALLOWED_IMAGE_TYPES.includes(fileType) && !hasValidExtension) {
      setImageError(t('onlyJpgJpegPngAccepted'));
      return;
    }

    if (file.size > MAX_MODEL_IMAGE_SIZE_BYTES) {
      setImageError(t('imageSizeExceedsLimit'));
      return;
    }

    setModelMedia([file]);
  };

  /**
   * Discards the newly selected image and reverts back to the existing image or empty state.
   */
  const handleRevertNewImage = () => {
    setModelMedia([]);
    setImageError('');
    if (fileInputReference.current) {
      fileInputReference.current.value = '';
    }
  };

  /**
   * Handles closing the drawer and resetting error and media state.
   */
  const handleClose = () => {
    setBrandError('');
    setModelNameError('');
    setImageError('');
    // setSecurityError(''); — SECURITY PROFILES DISABLED
    setShowFormErrors(false);
    setModelMedia([]);
    if (fileInputReference.current) {
      fileInputReference.current.value = '';
    }
    onClose();
  };

  /**
   * Validates and submits the model update PATCH request.
   * Supports multipart/form-data when a new image is selected.
   * @returns {Promise<void>} Resolves when submit operation completes.
   */
  const handleSubmit = async (): Promise<void> => {
    setShowFormErrors(true);
    setBrandError('');
    setModelNameError('');
    // setSecurityError(''); — SECURITY PROFILES DISABLED

    if (!brandSelect) return;
    if (!modelName.trim()) return;
    if (diagnosticProtocolTypes.length === 0) return;
    if (firmwareProtocolTypes.length === 0) return;
    // ⚠ SECURITY PROFILES DISABLED — backend not deployed yet (frontend-only rollout,
    // PR #1130/#1143). Re-enable by uncommenting this block once backend security
    // endpoints/fields ship.
    /*
    if (Object.keys(validateModelSecurityForm(security)).length > 0) return;

    // The whole security object replaces the stored one. With no profile ticked it is
    // omitted, so the stored capability is kept (there is no "un-profile" contract yet).
    const securityPayload = buildModelSecurityPayload(security);
    */

    setSubmitting(true);

    const hasFile = modelMedia.length > 0 && modelMedia[0] instanceof File;

    const selectedBrandOption = brandOptions.find((b) => b.value === brandSelect);
    const selectedBrandItem = brandsList.find((b) => b._id === brandSelect);
    const selectedBrandName = selectedBrandOption?.label || selectedBrandItem?.name || brandSelect;

    let dispatchPayload: { id: string; data: any; isFileUpload?: boolean };

    if (hasFile) {
      const formData = new FormData();
      formData.append('name', modelName.trim());
      formData.append('brandId', brandSelect);
      formData.append('brandName', selectedBrandName);
      formData.append('modelImage', modelMedia[0]);
      diagnosticProtocolTypes.forEach((protocol, index) => {
        formData.append(`diagnosticProtocolTypes[${index}]`, protocol);
      });
      firmwareProtocolTypes.forEach((protocol, index) => {
        formData.append(`firmwareProtocolTypes[${index}]`, protocol);
      });
      // appendSecurityToFormData(formData, securityPayload); — SECURITY PROFILES DISABLED
      dispatchPayload = {
        id: modelData._id || modelData.id,
        data: formData,
        isFileUpload: true,
      };
    } else {
      dispatchPayload = {
        id: modelData._id || modelData.id,
        data: {
          name: modelName.trim(),
          brandId: brandSelect,
          diagnosticProtocolTypes,
          firmwareProtocolTypes,
          brandName: selectedBrandName,
          // ...(securityPayload && { security: securityPayload }), — SECURITY PROFILES DISABLED
        },
        isFileUpload: false,
      };
    }

    try {
      await dispatch(editModel(dispatchPayload)).unwrap();

      dispatch(
        fetchAllModels({
          page: 1,
          limit: 10,
          sortBy: 'desc',
          sortField: 'createdAt',
        }),
      );

      setShowSuccess(true);
      onModelUpdated();
    } catch (error: any) {
      const message = error?.message || error?.response?.data?.message || t('failedToUpdateModel');
      const lowerMessage = message.toLowerCase();

      let translatedMessage = message;
      if (lowerMessage.includes('"imageurl" is not allowed') || lowerMessage.includes('imageurl is not allowed')) {
        translatedMessage = t('imageUrlNotAllowed');
      }

      // if (isModelSecurityError(message)) { — SECURITY PROFILES DISABLED
      //   setSecurityError(message);
      // } else
      if (
        lowerMessage.includes('model') ||
        lowerMessage.includes('already exists') ||
        lowerMessage.includes('already taken')
      ) {
        setModelNameError(translatedMessage);
      } else if (lowerMessage.includes('brand') && !lowerMessage.includes('model')) {
        setBrandError(translatedMessage);
      } else {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 500,
              message: translatedMessage,
            },
          }),
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const activePreview = newImagePreviewUrl || existingImage || null;
  const isNewImage = Boolean(newImagePreviewUrl);

  return (
    <>
      <ModalBox
        open={open}
        title=""
        content={
          <div className="left-drawer-content">
            <div className="drawer-header">
              <h3 className="drawer-title">{t('editModel')}</h3>
              <p className="drawer-subtitle">{t('editModelSubtitle')}</p>
            </div>
            <div className="drawer-body">
              <SelectField
                name="editModelBrandSelect"
                inputLabel={t('brand')}
                options={brandOptions}
                placeholder={t('chooseBrand')}
                selectedValue={brandSelect}
                onChangeValue={(_, value) => {
                  setBrandSelect(value.toString());
                  if (brandError) setBrandError('');
                }}
                error={Boolean((showFormErrors && !brandSelect) || brandError)}
                errorMessage={brandError || t('brandRequired')}
                valueRequired
                enableSearch
                hideLabelNodeInSelected
                disabled
              />
              <CustomInput
                type="text"
                name="editModelName"
                inputLabel={t('modelName')}
                placeholder={t('enterModelName')}
                value={modelName}
                onChangeValue={(_, value) => {
                  setModelName(value.toString());
                  if (modelNameError) setModelNameError('');
                }}
                error={Boolean((showFormErrors && !modelName.trim()) || modelNameError)}
                errorMessage={modelNameError || t('modelNameRequired')}
                valueRequired
                characterLimit={100}
              />
              <div className="model-image-preview-section">
                <div className="preview-section-header">
                  <span className="input-label">
                    {t('uploadMedia')}
                    <TooltipIcon text={t('mediaModelTooltip')} />
                  </span>
                </div>
                <input
                  ref={fileInputReference}
                  type="file"
                  accept=".jpg,.jpeg,.png"
                  className="model-file-input-hidden"
                  onChange={(event) => {
                    const { files } = event.target;
                    if (files && files.length > 0) {
                      handleFileSelect(Array.from(files));
                    }
                    if (fileInputReference.current) {
                      fileInputReference.current.value = '';
                    }
                  }}
                />
                {activePreview ? (
                  <div className="model-image-preview-card">
                    <div className="preview-image-wrapper">
                      <img
                        src={activePreview}
                        alt={modelName || t('modelPreview')}
                        className="model-preview-image"
                      />
                    </div>
                    <div className="preview-card-footer">
                      <div className="preview-badge-wrap">
                        {isNewImage ? (
                          <span className="preview-badge new">{t('newImageSelected')}</span>
                        ) : (
                          <span className="preview-badge current">{t('currentImage')}</span>
                        )}
                      </div>
                      <div className="preview-actions">
                        <button
                          type="button"
                          className="action-btn"
                          onClick={() => fileInputReference.current?.click()}>
                          <UploadIcon />
                          <span>{t('change')}</span>
                        </button>
                        {isNewImage && (
                          <button
                            type="button"
                            className="action-btn danger"
                            onClick={handleRevertNewImage}>
                            <RemoveIcon />
                            <span>{t('revert')}</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div
                    className="model-image-empty-state"
                    role="presentation"
                    onClick={() => fileInputReference.current?.click()}
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={(event) => {
                      event.preventDefault();
                      const { files } = event.dataTransfer;
                      if (files && files.length > 0) {
                        handleFileSelect(Array.from(files));
                      }
                    }}>
                    <div className="empty-icon-wrap">
                      <FileUploadIcon />
                    </div>
                    <p className="empty-title">{t('noImageUploaded')}</p>
                    <p className="empty-subtitle">{t('selectJpgJpegPngMax1MB')}</p>
                    <button
                      type="button"
                      className="empty-upload-btn"
                      onClick={(event) => {
                        event.stopPropagation();
                        fileInputReference.current?.click();
                      }}>
                      <UploadIcon />
                      <span>{t('uploadImage')}</span>
                    </button>
                  </div>
                )}
                {imageError && <p className="model-image-error-text">{imageError}</p>}
              </div>

              <CheckBoxButtons
                inputLabel={t('diagnosticProtocolTypes')}
                name="diagnosticProtocolTypes"
                list={DIAGNOSTIC_PROTOCOL_TYPE_OPTIONS}
                value={diagnosticProtocolTypes}
                onChangeValue={(_, value) => setDiagnosisProtocolTypes(value as string[])}
                valueRequired
                error={showFormErrors && diagnosticProtocolTypes.length === 0}
                errorMessage={t('diagnosticProtocolTypesRequired')}
              />
              <CheckBoxButtons
                inputLabel={t('firmwareProtocolTypes')}
                name="firmwareProtocolTypes"
                list={FIRMWARE_PROTOCOL_TYPE_OPTIONS}
                value={firmwareProtocolTypes}
                onChangeValue={(_, value) => setFirmwareProtocolTypes(value as string[])}
                valueRequired
                error={showFormErrors && firmwareProtocolTypes.length === 0}
                errorMessage={t('firmwareProtocolTypesRequired')}
              />
              {/* ⚠ SECURITY PROFILES DISABLED — backend not deployed yet (frontend-only
                  rollout, PR #1130/#1143). Re-enable by uncommenting this block once backend
                  security endpoints/fields ship.
              <ModelSecurityFields
                value={security}
                onChange={(next) => {
                  setSecurity(next);
                  if (securityError) setSecurityError('');
                }}
                showErrors={showFormErrors}
                serverError={securityError}
              /> */}
            </div>
            <div className="drawer-footer">
              <MyButton label={t('cancel')} buttonType="error" onClickFunc={handleClose} />
              <MyButton
                label={t('updateModel')}
                buttonType="submit"
                onClickFunc={handleSubmit}
                disableButton={submitting}
                loading={submitting}
              />
            </div>
          </div>
        }
        closeModal={handleClose}
        header={false}
        position="right"
        modalBoxAnimation="modalAnimateFromRight"
      />
      <ModalBox
        open={showSuccess}
        title=""
        content={
          <SuccessNotification
            title={t('modelUpdatedSuccessfully')}
            description={t('modelUpdatedDescription')}
            onNotificationClose={() => {
              setShowSuccess(false);
              onClose();
            }}
          />
        }
        closeModal={() => {
          setShowSuccess(false);
          onClose();
        }}
        header={false}
        notification
      />
    </>
  );
}

export default EditModelDrawer;
